import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { productsAPI } from '../services/api';
import Header from './Header';
import Footer from './Footer';
import RecommendedProducts from './RecommendedProducts';
import ProductFAQ from './ProductFAQ';
import BenefitsTargetOrgans from './BenefitsTargetOrgans';
import ProductReviews from './ProductReviews';
import FreeShippingBadge from './FreeShippingBadge';
import SubscriptionToggle from './SubscriptionToggle';
import ShippingRibbon from './ShippingRibbon';
import SEOHead from './SEOHead';
import SubstituteBadge from './SubstituteBadge';
import NotFoundPage from './NotFoundPage';
import { getLocalizedProductType } from '../utils/productTypeTranslations';
import { trackViewItem } from '../utils/analytics';
import { generateTopFold, generateTopFoldPlain } from '../utils/topFoldGenerator';
import axios from 'axios';
import { Button } from './ui/button';
import { ChevronLeft, Minus, Plus, ShoppingCart, Shield, Truck, FlaskConical, Check, AlertTriangle, Package, Award, Calculator } from 'lucide-react';

const ProductPage = () => {
  const { id } = useParams();
  const { t, currentLanguage } = useLanguage();
  const { addToCart, loading: cartLoading } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [purchaseMode, setPurchaseMode] = useState('one_time'); // 'one_time' | 'subscription'
  const [subLoading, setSubLoading] = useState(false);
  const [reviewSummary, setReviewSummary] = useState(null);
  // Pillar 3 of AI-search strategy: category name (English) used to seed
  // the citable-chunk top-fold paragraph (`generateTopFold`). Fetched
  // lazily once the product loads so we have its category_id.
  const [categoryName, setCategoryName] = useState('');

  const handleSubscribe = async () => {
    try {
      setSubLoading(true);
      const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/subscriptions/checkout`, {
        product_id: product.id,
        variant_label: selectedVariant?.label || selectedVariant?.size || null,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        alert('Unable to start subscription checkout.');
      }
    } catch (err) {
      alert(`Subscription checkout failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setSubLoading(false);
    }
  };

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const productData = await productsAPI.getById(id);
        setProduct(productData);
        
        if (productData.variants && productData.variants.length > 0) {
          setSelectedVariant(productData.variants[0]);
        }
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Product not found');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  // Fetch the public review summary in parallel for legitimate aggregateRating schema.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/products/${id}/reviews/summary`);
        if (!cancelled) setReviewSummary(r.data);
      } catch (e) {
        // silent
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // GA4 view_item + Yandex.Metrica ecommerce.detail + reachGoal('view_item').
  // Critical for Google Ads Performance Max + DV360 audience modelling
  // (signals which products a visitor inspected, even without add-to-cart).
  // Fires once per product load; uses product.id as the de-dup key so
  // re-renders don't double-fire.
  useEffect(() => {
    if (!product || !product.id) return;
    try {
      trackViewItem(product, currentLanguage);
    } catch (_) { /* analytics never blocks UX */ }
  }, [product?.id, currentLanguage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch the category name (English) once the product loads so the
  // top-fold helper can localise the category fragment. Lightweight, 
  // ~8 categories total, served from a tiny endpoint.
  useEffect(() => {
    if (!product?.category_id) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/categories/${product.category_id}`);
        if (!cancelled && r.data?.name?.en) setCategoryName(r.data.name.en);
      } catch (_) { /* silent, top-fold falls back to generic phrasing */ }
    })();
    return () => { cancelled = true; };
  }, [product?.category_id]);

  // ---- Facebook/Pinterest Product Open Graph meta tags (iter-52 follow-up) ----
  // Powers Pinterest Rich Pins, Facebook Shop catalog imports, and increasingly
  // enriches Google Merchant Center / Google Shopping thumbnails via the
  // <meta property="product:*"> family. SEOHead.js handles og:title/desc/image
  // sitewide; THIS effect adds the product-specific Open Graph fields that
  // only make sense on /product/{id} routes.
  //
  // Imperative because react-helmet-async drops some <meta property=...>
  // children on locale switches (observed iter-51). Idempotent, every tag
  // is marked with data-plp-og-product=1 and replaced on re-run. Unmount
  // cleanup is critical: when the user SPA-navigates away from a product
  // page, these tags must be removed so the next page (e.g., /blog) doesn't
  // inherit a stale product:price meta.
  useEffect(() => {
    if (!product) return undefined;
    const minPrice = product.variants && product.variants.length > 0
      ? Math.min(...product.variants.map(v => v.price))
      : (product.price || 0);
    const tags = [
      // Override og:type from the default "website" (set in index.html) to
      // "product". Facebook + Pinterest only treat this page as a shoppable
      // entity when og:type === "product".
      ['meta', { property: 'og:type', content: 'product' }],
      ['meta', { property: 'product:price:amount', content: minPrice.toFixed(2) }],
      ['meta', { property: 'product:price:currency', content: 'EUR' }],
      ['meta', { property: 'product:availability', content: 'in stock' }],
      ['meta', { property: 'product:condition', content: 'new' }],
      ['meta', { property: 'product:retailer_item_id', content: product.id }],
      ['meta', { property: 'product:brand', content: 'Penlab Peptide' }],
      // Twitter Card product summary, Pinterest, X/Twitter, and LinkedIn use
      // these as fallback when og:type=product unfurls in their card system.
      ['meta', { name: 'twitter:label1', content: 'Price' }],
      ['meta', { name: 'twitter:data1',  content: `EUR ${minPrice.toFixed(2)}` }],
      ['meta', { name: 'twitter:label2', content: 'Availability' }],
      ['meta', { name: 'twitter:data2',  content: 'In stock' }],
    ];
    try {
      // Clear any prior product OG tags first (idempotency)
      document.querySelectorAll('[data-plp-og-product="1"]').forEach((el) => el.remove());
      const head = document.head || document.getElementsByTagName('head')[0];
      // We REPLACE the static index.html og:type=website tag so Facebook
      // sees "product". Track the previous content so unmount can restore it.
      const ogTypeEl = document.querySelector('meta[property="og:type"]:not([data-plp-og-product])');
      const prevOgType = ogTypeEl ? ogTypeEl.getAttribute('content') : 'website';
      if (ogTypeEl) ogTypeEl.setAttribute('content', 'product');
      ogTypeEl && ogTypeEl.setAttribute('data-plp-og-type-overridden', '1');

      for (const [tagName, attrs] of tags) {
        // Skip og:type, we mutated the existing tag in place above.
        if (attrs.property === 'og:type') continue;
        const el = document.createElement(tagName);
        for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
        el.setAttribute('data-plp-og-product', '1');
        head.appendChild(el);
      }

      return () => {
        // Restore og:type=website on SPA-nav away from product detail.
        const restored = document.querySelector('meta[property="og:type"][data-plp-og-type-overridden="1"]');
        if (restored) {
          restored.setAttribute('content', prevOgType || 'website');
          restored.removeAttribute('data-plp-og-type-overridden');
        }
        document.querySelectorAll('[data-plp-og-product="1"]').forEach((el) => el.remove());
      };
    } catch (_) {
      return undefined;
    }
  }, [product]);

  const getProductName = () => {
    if (!product) return '';
    return product.name[currentLanguage] || product.name.en || product.name;
  };

  const getProductDescription = () => {
    if (!product?.description) return '';
    return product.description[currentLanguage] || product.description.en || '';
  };

  const { formatPrice } = useCurrency();

  // 9-language UI labels for this page, extends prior EN/ES/RU ternaries
  const LABELS = {
    en: { licensedTitle: 'Licensed Professionals Only:', licensedBody: 'We exclusively supply laboratories, clinics, and authorized institutions. KYC verification required (24-48h).', supplier: 'Supplier', selectSize: 'Select Size', from: 'From', taxIncluded: 'Tax Included', researchOnly: 'Research purposes only', quantity: 'Quantity:', redirecting: 'Redirecting...', subscribe: 'Subscribe monthly (−10%)', adding: 'Adding...', addToCart: 'Add to Cart', delivery: '3-5 day delivery', securePayment: 'Secure payment', stableFormula: 'Stabilized formula', description: 'Product Description', researchDisclaimer: 'This product is for research purposes only. Not for human consumption.', detailedInfo: 'Detailed Information', backToProducts: 'Back to all products', altSuffix: 'High-purity research peptide - PenLab Peptide' },
    es: { licensedTitle: 'Solo Profesionales Autorizados:', licensedBody: 'Suministramos exclusivamente a laboratorios, clínicas e instituciones autorizadas. Verificación KYC requerida (24-48h).', supplier: 'Proveedor', selectSize: 'Seleccionar Tamaño', from: 'Desde', taxIncluded: 'Impuestos incluidos', researchOnly: 'Solo para investigación', quantity: 'Cantidad:', redirecting: 'Redirigiendo...', subscribe: 'Suscribirse mensualmente (−10%)', adding: 'Añadiendo...', addToCart: 'Añadir al Carrito', delivery: 'Entrega 3-5 días', securePayment: 'Pago seguro', stableFormula: 'Fórmula estabilizada', description: 'Descripción del Producto', researchDisclaimer: 'Este producto es solo para fines de investigación. No apto para consumo humano.', detailedInfo: 'Información Detallada', backToProducts: 'Volver a todos los productos', altSuffix: 'Péptido de investigación de alta pureza - PenLab Peptide España' },
    ru: { licensedTitle: 'Только для лицензированных специалистов:', licensedBody: 'Мы осуществляем поставки исключительно лабораториям, клиникам и аккредитованным учреждениям. Требуется KYC-верификация (24-48 ч).', supplier: 'Поставщик', selectSize: 'Выберите Размер', from: 'От', taxIncluded: 'Включая налоги', researchOnly: 'Только для исследований', quantity: 'Количество:', redirecting: 'Перенаправление...', subscribe: 'Оформить подписку (−10%)', adding: 'Добавление...', addToCart: 'Добавить в Корзину', delivery: 'Доставка 3-5 дня', securePayment: 'Безопасная оплата', stableFormula: 'Стабилизированная формула', description: 'Описание Продукта', researchDisclaimer: 'Этот продукт предназначен только для исследовательских целей. Не для употребления человеком.', detailedInfo: 'Подробная Информация', backToProducts: 'Вернуться ко всем товарам', altSuffix: 'Исследовательский пептид высокой чистоты - PenLab Peptide' },
    de: { licensedTitle: 'Nur für lizenzierte Fachleute:', licensedBody: 'Wir beliefern ausschließlich Labore, Kliniken und autorisierte Institutionen. KYC-Verifizierung erforderlich (24-48 h).', supplier: 'Lieferant', selectSize: 'Größe auswählen', from: 'Ab', taxIncluded: 'Inkl. Steuern', researchOnly: 'Nur zu Forschungszwecken', quantity: 'Menge:', redirecting: 'Weiterleitung...', subscribe: 'Monatlich abonnieren (−10 %)', adding: 'Hinzufügen...', addToCart: 'In den Warenkorb', delivery: '3-5 Tage Lieferung', securePayment: 'Sichere Zahlung', stableFormula: 'Stabilisierte Formel', description: 'Produktbeschreibung', researchDisclaimer: 'Dieses Produkt ist ausschließlich für Forschungszwecke bestimmt. Nicht zum menschlichen Verzehr geeignet.', detailedInfo: 'Detaillierte Informationen', backToProducts: 'Zurück zu allen Produkten', altSuffix: 'Hochreines Forschungspeptid, PenLab Peptide' },
    fr: { licensedTitle: 'Professionnels agréés uniquement :', licensedBody: 'Nous fournissons exclusivement les laboratoires, cliniques et institutions agréées. Vérification KYC requise (24-48 h).', supplier: 'Fournisseur', selectSize: 'Choisir la taille', from: 'À partir de', taxIncluded: 'Taxes incluses', researchOnly: 'Usage de recherche uniquement', quantity: 'Quantité :', redirecting: 'Redirection...', subscribe: "S'abonner mensuellement (−10 %)", adding: 'Ajout...', addToCart: 'Ajouter au panier', delivery: 'Livraison en 3-5 jours', securePayment: 'Paiement sécurisé', stableFormula: 'Formule stabilisée', description: 'Description du produit', researchDisclaimer: 'Ce produit est destiné uniquement à la recherche. Non destiné à la consommation humaine.', detailedInfo: 'Informations détaillées', backToProducts: 'Retour à tous les produits', altSuffix: 'Peptide de recherche haute pureté, PenLab Peptide' },
    it: { licensedTitle: 'Solo professionisti autorizzati:', licensedBody: 'Forniamo esclusivamente a laboratori, cliniche e istituzioni autorizzate. Verifica KYC richiesta (24-48 h).', supplier: 'Fornitore', selectSize: 'Seleziona taglia', from: 'Da', taxIncluded: 'Tasse incluse', researchOnly: 'Solo per uso di ricerca', quantity: 'Quantità:', redirecting: 'Reindirizzamento...', subscribe: 'Abbonati mensilmente (−10%)', adding: 'Aggiungo...', addToCart: 'Aggiungi al carrello', delivery: 'Consegna in 3-5 giorni', securePayment: 'Pagamento sicuro', stableFormula: 'Formula stabilizzata', description: 'Descrizione del prodotto', researchDisclaimer: 'Questo prodotto è destinato esclusivamente alla ricerca. Non per consumo umano.', detailedInfo: 'Informazioni dettagliate', backToProducts: 'Torna a tutti i prodotti', altSuffix: 'Peptide di ricerca ad alta purezza, PenLab Peptide' },
    pl: { licensedTitle: 'Wyłącznie dla licencjonowanych specjalistów:', licensedBody: 'Dostarczamy wyłącznie do laboratoriów, klinik i autoryzowanych instytucji. Wymagana weryfikacja KYC (24-48 h).', supplier: 'Dostawca', selectSize: 'Wybierz rozmiar', from: 'Od', taxIncluded: 'Z podatkiem', researchOnly: 'Wyłącznie do celów badawczych', quantity: 'Ilość:', redirecting: 'Przekierowanie...', subscribe: 'Subskrybuj miesięcznie (−10%)', adding: 'Dodawanie...', addToCart: 'Dodaj do koszyka', delivery: 'Dostawa 3-5 dni', securePayment: 'Bezpieczna płatność', stableFormula: 'Stabilizowana formuła', description: 'Opis produktu', researchDisclaimer: 'Produkt przeznaczony wyłącznie do celów badawczych. Nie do spożycia przez ludzi.', detailedInfo: 'Szczegółowe informacje', backToProducts: 'Wróć do wszystkich produktów', altSuffix: 'Wysokiej czystości peptyd badawczy, PenLab Peptide' },
    nl: { licensedTitle: 'Alleen voor gelicentieerde professionals:', licensedBody: 'Wij leveren uitsluitend aan laboratoria, klinieken en geautoriseerde instellingen. KYC-verificatie vereist (24-48 u).', supplier: 'Leverancier', selectSize: 'Kies maat', from: 'Vanaf', taxIncluded: 'Incl. btw', researchOnly: 'Alleen voor onderzoeksdoeleinden', quantity: 'Aantal:', redirecting: 'Doorverwijzen...', subscribe: 'Maandelijks abonneren (−10%)', adding: 'Toevoegen...', addToCart: 'In winkelwagen', delivery: 'Levering 3-5 dagen', securePayment: 'Veilig betalen', stableFormula: 'Gestabiliseerde formule', description: 'Productbeschrijving', researchDisclaimer: 'Dit product is uitsluitend voor onderzoeksdoeleinden. Niet voor menselijke consumptie.', detailedInfo: 'Gedetailleerde informatie', backToProducts: 'Terug naar alle producten', altSuffix: 'Hoogzuiver onderzoekspeptide, PenLab Peptide' },
    pt: { licensedTitle: 'Apenas para profissionais licenciados:', licensedBody: 'Fornecemos exclusivamente a laboratórios, clínicas e instituições autorizadas. Verificação KYC necessária (24-48 h).', supplier: 'Fornecedor', selectSize: 'Selecionar tamanho', from: 'A partir de', taxIncluded: 'Impostos incluídos', researchOnly: 'Apenas para fins de pesquisa', quantity: 'Quantidade:', redirecting: 'Redirecionando...', subscribe: 'Assinar mensalmente (−10%)', adding: 'Adicionando...', addToCart: 'Adicionar ao carrinho', delivery: 'Entrega em 3-5 dias', securePayment: 'Pagamento seguro', stableFormula: 'Fórmula estabilizada', description: 'Descrição do produto', researchDisclaimer: 'Este produto destina-se apenas a fins de pesquisa. Não para consumo humano.', detailedInfo: 'Informações detalhadas', backToProducts: 'Voltar a todos os produtos', altSuffix: 'Peptídeo de pesquisa de alta pureza, PenLab Peptide' },
  };
  const L = LABELS[currentLanguage] || LABELS.en;

  const getCurrentPrice = () => {
    if (selectedVariant) {
      return selectedVariant.price;
    }
    return product?.price || 0;
  };

  const getCurrentDosage = () => {
    if (selectedVariant) {
      return selectedVariant.dosage;
    }
    return product?.dosage || '';
  };

  const handleAddToCart = async () => {
    const cartProduct = {
      ...product,
      price: getCurrentPrice(),
      selectedDosage: getCurrentDosage()
    };
    await addToCart(cartProduct, quantity);
  };

  const incrementQuantity = () => setQuantity(prev => prev + 1);
  const decrementQuantity = () => setQuantity(prev => Math.max(1, prev - 1));

  if (loading) {
    return (
      <div className="min-h-screen bg-capsule-bg">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="animate-pulse">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div className="bg-white/50 rounded-3xl h-96"></div>
              <div className="space-y-4">
                <div className="h-8 bg-white/50 rounded-2xl w-3/4"></div>
                <div className="h-4 bg-white/50 rounded-2xl w-1/2"></div>
                <div className="h-24 bg-white/50 rounded-2xl"></div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !product) {
    // Render the localized 404 component which emits `noindex, follow` and
    // strips the IIFE-injected self-canonical. Critical for SEO: ~224 product
    // UUIDs Google had indexed have been removed from the DB (db re-seed
    // regression). Without `noindex` here, Google keeps the dead URLs in its
    // index for 3-6 months. With it, de-indexing accelerates to days.
    return <NotFoundPage />;
  }

  const hasVariants = product.variants && product.variants.length > 1;

  return (
    <div className="min-h-screen bg-capsule-bg">
      <SEOHead 
        page="products"
        productName={getProductName()}
        productDescription={getProductDescription()?.substring(0, 160)}
        imageUrl={product?.image_url}
      />

      {/* Schema.org Product structured data + hreflang for global SEO.
          IMPORTANT: This is the SINGLE authoritative client-side Product
          schema. The duplicate stripped-down block previously emitted from
          SEOHead.js was removed in iter-42 because Google rejects pages
          carrying two competing Product blobs (the richer one wins, but the
          rejection blocks rich-result eligibility entirely while the
          conflict exists). Keep this in sync with the bot-prerender path in
          `backend/crawler_prerender.py::_product_meta`. */}
      {product && (() => {
        const baseUrl = (typeof window !== 'undefined' ? window.location.origin : 'https://penlabpeptide.com');
        const path = `/product/${product.id}`;
        // BCP-47 mapping mirroring backend HTML_LANG_MAP, `inLanguage` must
        // match the page's <html lang> for Google to surface the right copy
        // in mixed-locale search results (Google Shopping uses it directly).
        const bcp47 = ({
          en: 'en-US', es: 'es-ES', ru: 'ru-RU', de: 'de-DE', fr: 'fr-FR',
          it: 'it-IT', pl: 'pl-PL', nl: 'nl-NL', pt: 'pt-PT', au: 'en-AU',
        })[currentLanguage] || currentLanguage;
        const minPrice = product.variants && product.variants.length > 0
          ? Math.min(...product.variants.map(v => v.price))
          : (product.price || 0);
        const maxPrice = product.variants && product.variants.length > 0
          ? Math.max(...product.variants.map(v => v.price))
          : (product.price || 0);
        // Required by Google Merchant Listings rich result
        const priceValidUntil = (() => {
          const d = new Date(); d.setFullYear(d.getFullYear() + 1);
          return d.toISOString().split('T')[0];
        })();
        const returnPolicy = {
          "@type": "MerchantReturnPolicy",
          "applicableCountry": ["ES", "DE", "FR", "IT", "NL", "BE", "PT", "GB", "US", "AU"],
          "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
          "merchantReturnDays": 14,
          "returnMethod": "https://schema.org/ReturnByMail",
          "returnFees": "https://schema.org/FreeReturn"
        };
        const shippingDetails = {
          "@type": "OfferShippingDetails",
          "shippingRate": {
            "@type": "MonetaryAmount",
            "value": "25.00",
            "currency": "EUR"
          },
          "shippingDestination": [
            { "@type": "DefinedRegion", "addressCountry": ["ES", "DE", "FR", "IT", "NL", "BE", "PT", "AT", "PL", "CZ", "SK", "HU", "SE", "DK", "FI", "IE", "GR", "RO", "BG", "HR", "SI", "EE", "LV", "LT", "MT", "CY"] }
          ],
          "deliveryTime": {
            "@type": "ShippingDeliveryTime",
            "handlingTime": { "@type": "QuantitativeValue", "minValue": 1, "maxValue": 2, "unitCode": "DAY" },
            "transitTime":  { "@type": "QuantitativeValue", "minValue": 3, "maxValue": 7, "unitCode": "DAY" }
          }
        };
        const offerCommon = {
          "priceCurrency": "EUR",
          "priceValidUntil": priceValidUntil,
          "availability": "https://schema.org/InStock",
          "itemCondition": "https://schema.org/NewCondition",
          "url": `${baseUrl}${path}`,
          "seller": { "@type": "Organization", "name": "Penlab Peptide" },
          "hasMerchantReturnPolicy": returnPolicy,
          "shippingDetails": shippingDetails,
        };
        const offers = product.variants && product.variants.length > 0 ? {
          "@type": "AggregateOffer",
          "lowPrice": minPrice.toFixed(2),
          "highPrice": maxPrice.toFixed(2),
          "offerCount": product.variants.length,
          ...offerCommon,
        } : {
          "@type": "Offer",
          "price": (product.price || 0).toFixed(2),
          ...offerCommon,
        };
        // Compute the citation-grade top-fold paragraph (Pillar 3 of AI-
        // search strategy). Prepended to the JSON-LD description so AI
        // crawlers see the LLM-citable chunk FIRST, before the longer
        // product copy. Plain-text variant (no ** markers) for the JSON-LD.
        const topFoldPlain = generateTopFoldPlain(product, currentLanguage, categoryName);
        const baseDesc = getProductDescription() || '';
        const ldDescription = (topFoldPlain
          ? `${topFoldPlain}\n\n${baseDesc}`
          : baseDesc).substring(0, 5000);

        const ld = {
          "@context": "https://schema.org/",
          "@type": "Product",
          "name": getProductName(),
          "description": ldDescription || getProductName(),
          // BCP-47 form matching <html lang> + og:locale. Surfaces the active
          // locale to Google Shopping + rich-snippet renderers.
          "inLanguage": bcp47,
          "image": product.image_url ? [product.image_url] : [],
          "brand": { "@type": "Brand", "name": "Penlab Peptide" },
          "manufacturer": { "@type": "Organization", "name": "Penlab Peptide" },
          "category": "Research Chemical",
          "sku": product.id,
          "mpn": product.id,
          "gtin": product.id,
          "productID": product.id,
          "url": `${baseUrl}${path}`,
          // GMC pharma-classifier safeguards (mirrors
          // seo_locales.gmc_safeguard_additional_properties on the backend).
          // Tells Google's NLP this is a Research Chemical / Lab Reagent, 
          // NOT a drug or supplement, preventing GMC pharma auto-disapproval.
          "additionalProperty": [
            { "@type": "PropertyValue", "name": "researchUseOnly", "value": true },
            { "@type": "PropertyValue", "name": "productCategory", "value": "Research Chemical" },
            { "@type": "PropertyValue", "name": "labReagent", "value": true },
            // B2B-bulk signal, Pillar 2 of AI-search strategy. Tells AI
            // rerankers this product is available in bulk procurement.
            { "@type": "PropertyValue", "name": "bulkAvailable", "value": true },
            { "@type": "PropertyValue", "name": "bulkDiscountUpTo", "value": "50%" },
            { "@type": "PropertyValue", "name": "bulkProcurementUrl", "value": "https://penlabpeptide.com/bulk" },
          ],
          // Audience signal, moves us above DTC supplement vendors in AI
          // citation ranking for B2B-clinic procurement queries.
          "audience": [
            { "@type": "MedicalAudience", "audienceType": "Researcher" },
            { "@type": "MedicalAudience", "audienceType": "Clinician" },
            { "@type": "BusinessAudience", "audienceType": "Research Laboratory" },
          ],
          // Reinforces the classifier signal, paired with category=Research
          // Chemical + additionalProperty.researchUseOnly the page reads as
          // unambiguously non-therapeutic.
          "isRelatedTo": {
            "@type": "ChemicalSubstance",
            "name": getProductName(),
            "additionalType": "https://schema.org/ChemicalSubstance",
          },
          "offers": offers,
        };
        // AggregateRating only when the product actually has REAL approved reviews, Google penalises fake ratings.
        if (reviewSummary?.count > 0) {
          ld.aggregateRating = {
            "@type": "AggregateRating",
            "ratingValue": reviewSummary.average,
            "reviewCount": reviewSummary.count,
            "bestRating": 5,
            "worstRating": 1
          };
        } else if (product.rating && product.review_count) {
          ld.aggregateRating = {
            "@type": "AggregateRating",
            "ratingValue": product.rating,
            "reviewCount": product.review_count,
            "bestRating": 5,
            "worstRating": 1
          };
        }
        return (
          <>
            <script type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
            {/* hreflang alternates, subdirectory style, matching index.html
                and the per-locale sitemaps. Keeping these in sync with the
                bot-prerender canonical (`/{lang}/product/{id}`) is critical
                to avoid the "Duplicate without user-selected canonical" wave
                we cleaned up in iter-40. */}
            <link rel="alternate" hrefLang="en" href={`${baseUrl}/en${path}`} />
            <link rel="alternate" hrefLang="es" href={`${baseUrl}/es${path}`} />
            <link rel="alternate" hrefLang="ru" href={`${baseUrl}/ru${path}`} />
            <link rel="alternate" hrefLang="de" href={`${baseUrl}/de${path}`} />
            <link rel="alternate" hrefLang="fr" href={`${baseUrl}/fr${path}`} />
            <link rel="alternate" hrefLang="it" href={`${baseUrl}/it${path}`} />
            <link rel="alternate" hrefLang="pl" href={`${baseUrl}/pl${path}`} />
            <link rel="alternate" hrefLang="nl" href={`${baseUrl}/nl${path}`} />
            <link rel="alternate" hrefLang="pt" href={`${baseUrl}/pt${path}`} />
            <link rel="alternate" hrefLang="en-AU" href={`${baseUrl}/au${path}`} />
            <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${path}`} />
          </>
        );
      })()}

      <Header />
      
      {/* Breadcrumb, locale-aware labels. Hardcoded EN "Home / Products"
          on translated pages was visible body text Google saw on every
          /{lang}/product/X URL, contributing to the "Duplicate canonical:
          EN chosen" merge for 910 translated URLs (GSC). Now matches the
          locale-aware BreadcrumbList JSON-LD emitted by SEOHead.js. */}
      <div className="bg-white/50 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center text-sm text-gray-600">
            {(() => {
              const BC = {
                en: { home: 'Home', products: 'Products' },
                es: { home: 'Inicio', products: 'Productos' },
                ru: { home: 'Главная', products: 'Продукты' },
                de: { home: 'Startseite', products: 'Produkte' },
                fr: { home: 'Accueil', products: 'Produits' },
                it: { home: 'Home', products: 'Prodotti' },
                pl: { home: 'Strona główna', products: 'Produkty' },
                nl: { home: 'Home', products: 'Producten' },
                pt: { home: 'Início', products: 'Produtos' },
              };
              const b = BC[currentLanguage] || BC.en;
              return (
                <>
                  <Link to="/" className="hover:text-capsule-teal transition-colors">{b.home}</Link>
                  <span className="mx-2">/</span>
                  <Link to="/products" className="hover:text-capsule-teal transition-colors">{b.products}</Link>
                  <span className="mx-2">/</span>
                  <span className="text-gray-900 font-medium">{getProductName()}</span>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Licensed Professionals Banner */}
      <div className="bg-gradient-to-r from-capsule-teal/10 via-capsule-aqua/5 to-capsule-teal/10 border-b border-capsule-teal/20">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-center gap-3">
            <Shield className="w-5 h-5 text-capsule-teal flex-shrink-0" />
            <p className="text-sm text-gray-700 text-center">
              <strong>{L.licensedTitle}</strong> {L.licensedBody}
            </p>
          </div>
        </div>
      </div>

      {/* Product Details, single-column order per spec: Name → Image → Price/Cart → Delivery → Description */}
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <div className="space-y-6">

          {/* 1. Product Name */}
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              {getProductName()}
            </h1>
            <p className="text-gray-500">
              {L.supplier}: {product.supplier}
            </p>
            {product.product_type && (
              <span className="inline-block mt-2 px-4 py-1.5 bg-gradient-to-r from-capsule-teal/10 to-capsule-aqua/10 text-capsule-teal text-sm font-medium rounded-full border border-capsule-teal/20">
                {getLocalizedProductType(product.product_type, currentLanguage)}
              </span>
            )}
            <SubstituteBadge product={product} />
            {getCurrentDosage() && (
              <p className="text-capsule-teal font-medium mt-2">
                {getCurrentDosage()}
              </p>
            )}
          </div>

          {/* Variant/Dosage Selection, kept adjacent to the Name block because it determines which SKU price/image applies */}
          {hasVariants && (
            <div className="glass-card rounded-2xl p-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">
                {L.selectSize}
              </h3>
              <div className="flex flex-wrap gap-3">
                {product.variants.map((variant, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedVariant(variant)}
                    className={`relative px-5 py-3 rounded-xl border-2 transition-all duration-300 ${
                      selectedVariant?.dosage === variant.dosage
                        ? 'border-capsule-teal bg-gradient-to-r from-capsule-teal/10 to-capsule-aqua/10 text-capsule-teal shadow-teal-glow'
                        : 'border-gray-200 hover:border-capsule-teal/50 text-gray-700 bg-white'
                    }`}
                    data-testid={`variant-${index}`}
                  >
                    <div className="text-sm font-semibold">{variant.dosage}</div>
                    <div className="text-xs text-gray-500">{formatPrice(variant.price)}</div>
                    {selectedVariant?.dosage === variant.dosage && (
                      <div className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-capsule-teal to-capsule-aqua rounded-full p-1">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 2. Main Product Image */}
          <div className="glass-card rounded-3xl p-6 md:p-8">
            <img
              src={product.image_url || '/placeholder-product.png'}
              alt={`${getProductName()} - ${L.altSuffix}`}
              title={getProductName()}
              className="w-full h-auto max-h-[400px] object-contain rounded-2xl"
              loading="eager"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = '/placeholder-product.png';
              }}
            />
          </div>

          {/* 3. Price + Cart / Subscription Options */}
          <div className="glass-card rounded-2xl p-6">
            <div className="flex items-baseline gap-3">
              <span className="text-3xl md:text-4xl font-bold text-gray-900">
                {hasVariants ? (
                  <>
                    <span className="text-lg text-gray-500 font-normal mr-1">
                      {L.from}
                    </span>
                    {formatPrice(getCurrentPrice())}
                  </>
                ) : formatPrice(getCurrentPrice())}
              </span>
            </div>
            {/* Show current dosage/variant prominently */}
            {getCurrentDosage() && (
              <div className="mt-3 inline-flex items-center gap-2 bg-gradient-to-r from-capsule-teal/10 to-capsule-aqua/10 px-4 py-2 rounded-full border border-capsule-teal/20">
                <FlaskConical className="w-4 h-4 text-capsule-teal" />
                <span className="text-sm font-semibold text-capsule-teal">
                  {(() => {
                    // Localize non-numeric blend labels (Mix / Blend / Combo /
                    // Kit) so they don't render as English on every locale.
                    // Numeric dosages (5mg, 10mg, 100BIL/1ml etc) pass through.
                    const d = String(getCurrentDosage()).trim();
                    const DOSAGE_OVERLAY = {
                      Mix: { en: 'Mix', es: 'Mezcla', ru: 'Микс',
                             de: 'Mix', fr: 'Mélange', it: 'Mix',
                             pl: 'Mieszanka', nl: 'Mengsel', pt: 'Mistura', au: 'Mix' },
                      Blend: { en: 'Blend', es: 'Mezcla', ru: 'Смесь',
                               de: 'Mischung', fr: 'Mélange', it: 'Miscela',
                               pl: 'Mieszanka', nl: 'Mengsel', pt: 'Mistura', au: 'Blend' },
                      Combo: { en: 'Combo', es: 'Combo', ru: 'Комбо',
                               de: 'Combo', fr: 'Combo', it: 'Combo',
                               pl: 'Combo', nl: 'Combo', pt: 'Combo', au: 'Combo' },
                      Kit: { en: 'Kit', es: 'Kit', ru: 'Набор',
                             de: 'Kit', fr: 'Kit', it: 'Kit',
                             pl: 'Zestaw', nl: 'Kit', pt: 'Kit', au: 'Kit' },
                    };
                    const overlay = DOSAGE_OVERLAY[d];
                    return overlay ? (overlay[currentLanguage] || overlay.en) : d;
                  })()}
                </span>
              </div>
            )}
            {/* Free-shipping promise, always visible next to the price */}
            <div className="mt-3" data-testid="product-free-shipping-note">
              <FreeShippingBadge variant="inline" subtotalEur={getCurrentPrice() * quantity} />
            </div>
            <div className="flex items-center gap-4 mt-3">
              <p className="text-sm text-gray-500">
                {L.taxIncluded}
              </p>
              <span className="text-sm text-capsule-teal font-medium">
                {L.researchOnly}
              </span>
            </div>
          </div>

          {/* Subscription / one-time toggle */}
          <SubscriptionToggle
            productId={product?.id}
            // Pass the currently-selected variant's dosage as the label
            // (with legacy label/size fallbacks). Previously this only
            // sent `label || size`, but variants in our catalogue use
            // `dosage` as their key field, so it always sent `undefined`,
            // the API call dropped the query param, and the backend fell
            // back to the default product price. Result: ONE-TIME and
            // MONTHLY-SUBSCRIPTION cards were stuck on the default
            // variant's price regardless of the chip selection above.
            variantLabel={selectedVariant?.dosage || selectedVariant?.label || selectedVariant?.size}
            currentLanguage={currentLanguage}
            mode={purchaseMode}
            onModeChange={setPurchaseMode}
            className="mb-2"
          />

          {/* Quantity & Add to Cart / Subscribe */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">
                {L.quantity}
              </span>
              <div className="flex items-center glass-card rounded-xl overflow-hidden">
                <button
                  onClick={decrementQuantity}
                  className="p-3 hover:bg-white/50 transition-colors"
                  disabled={quantity <= 1}
                  data-testid="quantity-decrease"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-5 py-3 font-semibold bg-white" data-testid="quantity-value">{quantity}</span>
                <button
                  onClick={incrementQuantity}
                  className="p-3 hover:bg-white/50 transition-colors"
                  data-testid="quantity-increase"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {purchaseMode === 'subscription' ? (
              <Button
                onClick={handleSubscribe}
                disabled={subLoading}
                className="w-full bg-gradient-to-r from-capsule-coral to-capsule-salmon hover:opacity-90 text-white py-4 text-lg rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                data-testid="subscribe-btn"
              >
                {subLoading
                  ? L.redirecting
                  : L.subscribe}
              </Button>
            ) : (
              <Button
                onClick={handleAddToCart}
                disabled={cartLoading}
                className="w-full bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white py-4 text-lg rounded-full font-semibold shadow-teal-glow hover:shadow-lg transition-all duration-300"
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {cartLoading ? L.adding : L.addToCart}
              </Button>
            )}
          </div>

          {/* 4. Delivery Information */}
          <div className="space-y-3" data-testid="delivery-info">
            <ShippingRibbon currentLanguage={currentLanguage} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
            <div className="flex items-center gap-3 glass-card rounded-xl p-4">
              <div className="w-10 h-10 bg-gradient-to-br from-capsule-teal to-capsule-aqua rounded-lg flex items-center justify-center">
                <Truck className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm text-gray-700">
                {L.delivery}
              </span>
            </div>
            <div className="flex items-center gap-3 glass-card rounded-xl p-4">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm text-gray-700">
                {L.securePayment}
              </span>
            </div>
            <div className="flex items-center gap-3 glass-card rounded-xl p-4">
              <div className="w-10 h-10 bg-gradient-to-br from-capsule-coral to-capsule-salmon rounded-lg flex items-center justify-center">
                <FlaskConical className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm text-gray-700">
                {L.stableFormula}
              </span>
            </div>
            </div>
          </div>

          {/* Reconstitution Calculator CTA, only for GLP-1 peptides */}
          {(() => {
            const en = (product.name?.en || '').toLowerCase();
            const isReconRelevant = en.includes('tirzepatide') || en.includes('retatrutide') || en.includes('semaglutide');
            if (!isReconRelevant) return null;
            const ctaCopy = {
              en: { title: 'Reconstitution Calculator', body: 'Compute mg/mL concentration, mL per dose, and U-100 syringe units for this peptide in seconds.', cta: 'Open calculator' },
              es: { title: 'Calculadora de Reconstitución', body: 'Calcula concentración mg/mL, volumen por dosis y unidades en jeringa U-100 en segundos.', cta: 'Abrir calculadora' },
              ru: { title: 'Калькулятор разведения', body: 'Рассчитайте концентрацию мг/мл, объём на дозу и деления шприца U-100 за секунды.', cta: 'Открыть калькулятор' },
              de: { title: 'Rekonstitutions-Rechner', body: 'mg/mL-Konzentration, mL pro Dosis und U-100-Einheiten in Sekunden berechnen.', cta: 'Rechner öffnen' },
              fr: { title: 'Calculatrice de Reconstitution', body: 'Calculez concentration mg/mL, volume par dose et unités U-100 en quelques secondes.', cta: 'Ouvrir la calculatrice' },
              it: { title: 'Calcolatore di Ricostituzione', body: 'Calcola concentrazione mg/mL, volume per dose e unità U-100 in pochi secondi.', cta: 'Apri calcolatore' },
              pl: { title: 'Kalkulator Rozcieńczania', body: 'Oblicz stężenie mg/mL, objętość dawki i jednostki U-100 w kilka sekund.', cta: 'Otwórz kalkulator' },
              nl: { title: 'Reconstitutie-calculator', body: 'Bereken mg/mL-concentratie, mL per dosis en U-100-eenheden in seconden.', cta: 'Calculator openen' },
              pt: { title: 'Calculadora de Reconstituição', body: 'Calcule concentração mg/mL, volume por dose e unidades U-100 em segundos.', cta: 'Abrir calculadora' },
            };
            const c = ctaCopy[currentLanguage] || ctaCopy.en;
            return (
              <Link
                to="/calculators/reconstitution"
                className="block glass-card rounded-2xl p-5 border-l-4 border-teal-500 hover:shadow-lg transition-shadow"
                data-testid="product-page-recon-calc-link"
              >
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 bg-gradient-to-br from-teal-500 to-teal-700 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Calculator className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-gray-900 text-base">{c.title}</div>
                    <div className="text-sm text-gray-600 mt-1">{c.body}</div>
                    <span className="inline-block mt-2 text-sm font-semibold text-teal-700 hover:text-teal-900">
                      {c.cta} →
                    </span>
                  </div>
                </div>
              </Link>
            );
          })()}

          {/* 5. Product Description (including Benefits & Target Organs) */}
          <div className="space-y-6" data-testid="description-section">
            {/* Description */}
            <div className="glass-card rounded-3xl p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 pb-3 border-b border-gray-100">
                {L.description}
              </h2>
              {/* Citable-chunk top-fold (Pillar 3 of AI-search strategy).
                  This is the LLM-citable paragraph: fact-dense, 5 trust
                  signals, B2B framing, bulk-pricing hook, RUO
                  disambiguator. Rendered FIRST so AI crawlers (which
                  reranker-score by chunk) pick it up as the primary
                  citation. Rendered with **bold** markers converted to
                  <strong> for proper emphasis. */}
              {(() => {
                const md = generateTopFold(product, currentLanguage, categoryName);
                if (!md) return null;
                // Convert **bold** markers to <strong> spans. Single-pass
                // since the template never nests them.
                const parts = md.split(/(\*\*[^*]+\*\*)/g);
                return (
                  <p className="text-gray-800 leading-relaxed mb-6 p-4 bg-gradient-to-r from-capsule-teal/5 to-capsule-aqua/5 border-l-4 border-capsule-teal rounded-r-xl" data-testid="product-top-fold">
                    {parts.map((part, i) => part.startsWith('**') && part.endsWith('**')
                      ? <strong key={i} className="text-capsule-teal">{part.slice(2, -2)}</strong>
                      : <span key={i}>{part}</span>
                    )}
                  </p>
                );
              })()}
              <div className="text-gray-700 leading-relaxed space-y-4">
                {getProductDescription().split('\n\n').map((paragraph, idx) => {
                  const isDisclaimer = paragraph.includes('━━━') || paragraph.includes('⚠️') ||
                                       paragraph.includes('IMPORTANT DISCLAIMER') ||
                                       paragraph.includes('AVISO IMPORTANTE') ||
                                       paragraph.includes('ВАЖНОЕ ПРЕДУПРЕЖДЕНИЕ') ||
                                       paragraph.includes('laboratory purposes') ||
                                       paragraph.includes('fines de laboratorio') ||
                                       paragraph.includes('лабораторных целей');

                  if (isDisclaimer) {
                    const cleanText = paragraph
                      .replace(/━+/g, '')
                      .replace(/⚠️/g, '')
                      .replace(/IMPORTANT DISCLAIMER|AVISO IMPORTANTE|ВАЖНОЕ ПРЕДУПРЕЖДЕНИЕ/g, '')
                      .trim();

                    if (!cleanText) return null;

                    return (
                      <div key={idx} className="mt-6 p-4 bg-gradient-to-r from-capsule-teal/10 to-capsule-aqua/10 border-l-4 border-capsule-teal rounded-r-xl">
                        <p className="text-sm text-gray-600 italic">
                          {cleanText}
                        </p>
                      </div>
                    );
                  }

                  // Detect "What is X?" lead-paragraph across all 9 supported locales.
                  const leadPrefixes = ['What is', '¿Qué es', 'Что такое', 'Was ist', "Qu'est-ce que", 'Qu’est-ce que', "Cos'è", 'Cos’è', "Che cos'è", 'Che cos’è', 'Czym jest', 'Co to jest', 'Wat is', 'O que é'];
                  if (leadPrefixes.some(p => paragraph.startsWith(p))) {
                    return (
                      <div key={idx} className="mb-4">
                        <h3 className="text-lg font-semibold text-capsule-teal mb-3">{paragraph.split('\n')[0]}</h3>
                        <p className="text-gray-700">{paragraph.split('\n').slice(1).join('\n')}</p>
                      </div>
                    );
                  }

                  if (paragraph.includes('•')) {
                    const lines = paragraph.split('\n');
                    return (
                      <div key={idx} className="space-y-2">
                        {lines.map((line, lineIdx) => {
                          if (line.startsWith('•')) {
                            return (
                              <div key={lineIdx} className="flex items-start gap-2 ml-2">
                                <span className="text-capsule-teal mt-1">•</span>
                                <span className="text-gray-700">{line.substring(1).trim()}</span>
                              </div>
                            );
                          }
                          if (line.includes(':') && !line.startsWith('•') && line.length < 80 && line.trim()) {
                            return (
                              <h4 key={lineIdx} className="font-semibold text-gray-800 mt-5 mb-2 text-base">
                                {line}
                              </h4>
                            );
                          }
                          if (line.trim()) {
                            return <p key={lineIdx} className="text-gray-700">{line}</p>;
                          }
                          return null;
                        })}
                      </div>
                    );
                  }

                  if (paragraph.includes(':') && paragraph.length < 80 && !paragraph.includes('•')) {
                    const parts = paragraph.split(':');
                    if (parts.length === 2 && parts[0].length < 60) {
                      return (
                        <div key={idx}>
                          <h4 className="font-semibold text-gray-800 mt-4 mb-2">{parts[0]}:</h4>
                          <p className="text-gray-700">{parts[1]}</p>
                        </div>
                      );
                    }
                  }

                  return <p key={idx} className="text-gray-700 leading-relaxed">{paragraph}</p>;
                })}
              </div>
            </div>

            {/* Benefits & Target Organs, part of the Description section per spec */}
            <BenefitsTargetOrgans product={product} currentLanguage={currentLanguage} />

            {/* Research Disclaimer Banner */}
            <div className="bg-gray-900 text-white px-6 py-4 rounded-2xl">
              <p className="text-sm text-center">
                {L.researchDisclaimer}
              </p>
            </div>
          </div>
        </div>

        {/* Detailed Description Section - Only show if product has detailed_description */}
        {product?.detailed_description && (
          <div className="mt-8">
            <div className="glass-card rounded-3xl p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                {L.detailedInfo}
              </h2>
              <p className="text-gray-700 leading-relaxed">
                {product.detailed_description[currentLanguage] || product.detailed_description.en}
              </p>
            </div>
          </div>
        )}

        {/* FAQ Section - rule-based Q&A derived from seo_meta.long_description */}
        <ProductFAQ
          productId={product?.id}
          currentLanguage={currentLanguage}
          productName={getProductName()}
        />

        {/* Verified-purchase customer reviews + aggregateRating data */}
        <div className="mt-6">
          <ProductReviews productId={product?.id} />
        </div>

        {/* Back to Products */}
        <div className="mt-12">
          <Link
            to="/products"
            className="inline-flex items-center text-capsule-teal hover:text-capsule-teal-dark font-medium transition-colors"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            {L.backToProducts}
          </Link>
        </div>
      </div>

      {/* Recommended Products Section */}
      <RecommendedProducts 
        currentProductId={product?.id} 
        categoryId={product?.category_id} 
      />

      <Footer />
    </div>
  );
};

export default ProductPage;
